import apiClient from './ApiClient';

/**
 * Door locks / RFID key cards (backend door_locks app).
 *
 * The hotel's on-premise bridge encodes cards on the reception encoder;
 * these calls queue jobs for it and report their progress.
 * ApiClient already unwraps response.data, and get() takes the query
 * params object directly (not wrapped in { params }).
 */
class DoorLockService {
  /** Bridge configured / online status for the current hotel. */
  async getStatus() {
    return apiClient.get('/door-locks/status/');
  }

  /**
   * Queue key card(s) for a reservation room.
   * @param {number} reservationId
   * @param {{roomAssignmentId?: number, mode?: 'new'|'copy', cardQty?: number}} options
   */
  async issueKeyCards(reservationId, { roomAssignmentId, mode = 'new', cardQty = 1 } = {}) {
    const body = { mode, cardQty };
    if (roomAssignmentId) body.roomAssignmentId = roomAssignmentId;
    return apiClient.post(`/door-locks/reservations/${reservationId}/key-cards/`, body);
  }

  async getJob(jobId) {
    return apiClient.get(`/door-locks/jobs/${jobId}/`);
  }

  /** Jobs for one reservation (newest first). */
  async getReservationJobs(reservationId) {
    return apiClient.get('/door-locks/jobs/', { reservation: reservationId });
  }

  /** Today's jobs that need staff action (no card on encoder, errors). */
  async getAttentionJobs() {
    return apiClient.get('/door-locks/jobs/', { attention: 1 });
  }

  async retryJob(jobId) {
    return apiClient.post(`/door-locks/jobs/${jobId}/retry/`);
  }

  async cancelJob(jobId) {
    return apiClient.post(`/door-locks/jobs/${jobId}/cancel/`);
  }

  /** Read the card currently on the encoder. */
  async readCard() {
    return apiClient.post('/door-locks/read-card/');
  }
}

/** Job statuses that are still in progress. */
export const OPEN_JOB_STATUSES = ['pending', 'dispatched'];

export const isJobOpen = (job) => !!job && OPEN_JOB_STATUSES.includes(job.status);

/** Chakra color scheme for a job status. */
export const jobStatusColor = (status) => ({
  pending: 'gray',
  dispatched: 'blue',
  success: 'green',
  no_card: 'orange',
  transient: 'orange',
  expired: 'gray',
  cancelled: 'gray',
}[status] || 'red');

/**
 * Poll a job until it finishes.
 * @param {number} jobId
 * @param {{intervalMs?: number, timeoutMs?: number, onUpdate?: Function, signal?: {cancelled: boolean}}} opts
 * @returns {Promise<object>} the last job state seen
 */
export async function waitForJob(jobId, {
  intervalMs = 1500, timeoutMs = 90000, onUpdate, signal,
} = {}) {
  const started = Date.now();
  let job = await doorLockService.getJob(jobId);
  onUpdate?.(job);
  while (isJobOpen(job) && Date.now() - started < timeoutMs && !signal?.cancelled) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    if (signal?.cancelled) break;
    job = await doorLockService.getJob(jobId);
    onUpdate?.(job);
  }
  return job;
}

const doorLockService = new DoorLockService();
export default doorLockService;
